/**
 * FusionAuth OIDC Configuration for Report Summarizer
 * Based on patterns from manage-next and other HappyCo applications
 */

export interface OIDCConfig {
  authority: string
  clientId: string
  scope: string
  redirectUri: string
  postLogoutRedirectUri: string
  automaticSilentRenew: boolean
  loadUserInfo: boolean
  tenantId: string
}

/**
 * Determines the current environment based on hostname
 */
export function getEnvironment(): 'devlocal' | 'dev' | 'staging' | 'prod' {
  var envName = 'prod'
  if (window.location.origin.indexOf(':3000') > -1) {
    envName = 'devlocal'
  } else if (window.location.origin.indexOf('.dev') > -1) {
    envName = 'dev'
  } else if (window.location.origin.indexOf('staging') > -1) {
    envName = 'staging'
  }
  return envName as 'devlocal' | 'dev' | 'staging' | 'prod'
}

/**
 * Determines if authentication should be bypassed
 * In devlocal, auth is skipped by default unless VITE_ENABLE_AUTH is set to 'true'
 * In other environments, auth is always enabled
 */
export function shouldSkipAuth(): boolean {
  const env = getEnvironment()

  if (env === 'devlocal') {
    return import.meta.env.VITE_ENABLE_AUTH !== 'true'
  }

  return false
}

/**
 * Gets the FusionAuth authority URL based on environment
 */
export function getAuthorityUrl(): string {
  const env = getEnvironment()
  
  switch (env) {
    case 'devlocal':
      return 'http://localhost:9011'
    case 'dev':
      return 'https://auth.dev.happyco.com'
    case 'staging':
      return 'https://auth.staging.happyco.com'
    case 'prod':
      return 'https://auth.happyco.com'
    default:
      return 'http://localhost:9011'
  }
}

/**
 * Gets the tenant ID based on environment
 */
export function getTenantId(): string {
  const env = getEnvironment()
  
  switch (env) {
    case 'devlocal':
      return '8ef3d592-b612-4bfe-acff-9c403a9c7a97'
    case 'dev':
      return '5d1ea2e8-5af3-4427-ad59-4333f0ee27f3'
    case 'staging':
      return 'caf75e3f-ab95-443b-8815-00182aeb1066'
    case 'prod':
      return 'b8d1f4f9-4dbe-45b8-9a55-73cb6ac5dee1'
    default:
      return '8ef3d592-b612-4bfe-acff-9c403a9c7a97'
  }
}

/**
 * Gets the Report Summarizer client ID
 */
export function getClientId(): string {
  return '1c5e63f8-ca30-5bf9-b9a4-6bcd7d5b0b07'
}

/**
 * Creates the complete OIDC configuration
 */
export function createOIDCConfig(): OIDCConfig {
  const baseUrl = window.location.origin
  
  return {
    authority: getAuthorityUrl(),
    clientId: getClientId(),
    scope: 'openid profile email',
    redirectUri: `${baseUrl}/callback`,
    postLogoutRedirectUri: baseUrl,
    automaticSilentRenew: true,
    loadUserInfo: true,
    tenantId: getTenantId(),
  }
}
